import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { commonMessages } from 'app/i18n/messages'
import { SiteDeviceStats } from 'features/networkAi/types'
import {
  APListLineItemOuterFrame,
  APListLineItemNameInner,
  APListLineItemName,
  APListLineItemMac,
  APListLineItemUptime,
  APListLineItemVersion,
  APListLineItemIP,
  APListLineItemClients,
  APListLineItemBytes,
  APListLineItemTrash,
  APListLineItemStatusFrame,
  APListLineItemIconFrame,
  APListLineItemText,
  APListLineItemHightlighted,
  APListLineItemDeleteIcon,
  APListIndicator,
  APNetworkDeviceIcon,
} from './styles'
import { makePublicUrl } from 'app/utils/publicUrl'
import { mdiCheckCircleOutline, mdiAlertCircleOutline } from '@mdi/js'
import { deleteSiteClaimDeviceThunk } from 'features/networkAi/slice'
import { useAppDispatch } from 'app/hooks'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { networkAPMessages } from '../../messages'

import { mdiDeleteOutline } from '@mdi/js'
import theme from 'theme_'

interface APLineItemProps {
  siteDeviceStats: SiteDeviceStats
}

function APLineItem({ siteDeviceStats }: APLineItemProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const [isDeleteAPModalOpen, setIsDeleteAPModalOpen] = useState(false)

  const handleConfirmToDeleteAP = useCallback(() => {
    setIsDeleteAPModalOpen(true)
  }, [setIsDeleteAPModalOpen])

  const handleConfirmDeleteAP = useCallback(() => {
    dispatch(
      deleteSiteClaimDeviceThunk(
        siteDeviceStats.org_id,
        siteDeviceStats.mac,
        siteDeviceStats.mac,
        siteDeviceStats.site_id
      )
    )

    setIsDeleteAPModalOpen(false)
  }, [dispatch, siteDeviceStats, setIsDeleteAPModalOpen])

  function formatMac(mac: string) {
    return (
      mac.slice(0, 2) +
      ':' +
      mac.slice(2, 4) +
      ':' +
      mac.slice(4, 6) +
      ':' +
      mac.slice(6, 8) +
      ':' +
      mac.slice(8, 10) +
      ':' +
      mac.slice(10, 12)
    )
  }

  function formatBytes(bytes: number, decimals = 2) {
    if (!+bytes) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  }

  function formatTotalBytes() {
    const totalBytes = siteDeviceStats.rx_bytes + siteDeviceStats.tx_bytes
    return formatBytes(totalBytes, 2)
  }

  function formatUptime(uptime: number) {
    if (isNaN(uptime) || uptime == null) {
      return 'N/A'
    } else if (uptime >= 86400) {
      return Math.round(uptime / 86400) + ' days'
    } else if (uptime >= 3600) {
      return Math.round(uptime / 3600) + ' hours'
    } else {
      return Math.round(uptime / 60) + ' mins'
    }
  }

  return (
    <>
      <ConfirmModal
        isOpen={isDeleteAPModalOpen}
        title={formatMessage(networkAPMessages.titleDelete)}
        confirmBtnTitle={formatMessage(networkAPMessages.confirmDeleteMessage)}
        discardBtnTitle={formatMessage(commonMessages.cancel)}
        variant="primary"
        onConfirm={handleConfirmDeleteAP}
        onDiscard={() => setIsDeleteAPModalOpen(false)}
      >
        {formatMessage(commonMessages.promptConfirm)}
        {formatMessage(networkAPMessages.titleDelete)}{' '}
        <APListLineItemHightlighted>
          {siteDeviceStats.name}
        </APListLineItemHightlighted>
        ?
      </ConfirmModal>
      {siteDeviceStats.mac !== ' ' && (
        <APListLineItemOuterFrame>
          <APListLineItemName style={{ width: 200 }}>
            <APListLineItemStatusFrame>
              <APListIndicator
                path={
                  siteDeviceStats.status === 'connected'
                    ? mdiCheckCircleOutline
                    : mdiAlertCircleOutline
                }
                size={0.75}
                color={
                  siteDeviceStats.status === 'connected'
                    ? theme.colors.successGreen
                    : theme.colors.error
                }
              />
            </APListLineItemStatusFrame>

            <APListLineItemIconFrame>
              <APNetworkDeviceIcon
                src={makePublicUrl(`images/networkIcons/networkDevice.svg`)}
              />
            </APListLineItemIconFrame>

            <APListLineItemNameInner style={{ width: 275 }}>
              {siteDeviceStats.name}
            </APListLineItemNameInner>
          </APListLineItemName>

          <APListLineItemMac>
            <APListLineItemText style={{ width: 125 }}>
              {formatMac(siteDeviceStats.mac)}
            </APListLineItemText>
          </APListLineItemMac>

          <APListLineItemVersion>
            <APListLineItemText style={{ width: 130 }}>
              {siteDeviceStats.version}
            </APListLineItemText>
          </APListLineItemVersion>

          <APListLineItemIP>
            <APListLineItemText style={{ width: 110 }}>
              {siteDeviceStats.ip}
            </APListLineItemText>
          </APListLineItemIP>

          <APListLineItemUptime>
            <APListLineItemText style={{ width: 70 }}>
              {formatUptime(siteDeviceStats.uptime)}
            </APListLineItemText>
          </APListLineItemUptime>

          <APListLineItemClients>
            <APListLineItemText style={{ width: 75 }}>
              {siteDeviceStats.num_clients}
            </APListLineItemText>
          </APListLineItemClients>

          <APListLineItemBytes>
            <APListLineItemText style={{ width: 70 }}>
              {formatTotalBytes()}
            </APListLineItemText>
          </APListLineItemBytes>

          <APListLineItemTrash onClick={handleConfirmToDeleteAP}>
            <APListLineItemDeleteIcon
              path={mdiDeleteOutline}
              size={1}
              color={theme.colors.horizonOrange}
            />
          </APListLineItemTrash>
        </APListLineItemOuterFrame>
      )}
    </>
  )
}

export default APLineItem
